import {useEffect,useState} from 'react';
import {EmailIntakeSettingsSnapshot,loadEmailIntakeSettings} from './api';

const connectionLabels:Record<string,string>={
  consent_required:'Mailbox owner approval is still needed.',
  connected:'Mailbox setup is connected. This does not mean email is being processed.',
  expiring:'Mailbox approval may need renewal.',
  expired:'Mailbox approval has expired.',
  reconnect_required:'Mailbox setup needs attention.',
  revoked:'Mailbox access has been removed.',
};
const channelLabels:Record<string,string>={
  instructions_ready:'Forwarding setup details have been saved.',
  active:'Forwarding setup is marked active. Processing is shown separately below.',
  suspended:'Forwarding setup is suspended.',
  revoked:'Forwarding setup has been removed.',
};

function savedLabel(labels:Record<string,string>,value:string):string|undefined{return Object.prototype.hasOwnProperty.call(labels,value)?labels[value]:undefined;}

export function EmailIntakeSettings({organizationId}:{organizationId:string}){
  const [attempt,setAttempt]=useState(0);
  const [result,setResult]=useState<{organizationId:string;attempt:number;data:EmailIntakeSettingsSnapshot|null;failed:boolean}|null>(null);
  useEffect(()=>{
    let active=true;
    const controller=new AbortController();
    const timer=setTimeout(()=>{if(active){active=false;controller.abort();setResult({organizationId,attempt,data:null,failed:true});}},15000);
    void loadEmailIntakeSettings(organizationId,controller.signal).then(data=>{
      if(active){active=false;clearTimeout(timer);setResult({organizationId,attempt,data,failed:false});}
    },()=>{
      if(active){active=false;clearTimeout(timer);setResult({organizationId,attempt,data:null,failed:true});}
    });
    return ()=>{active=false;clearTimeout(timer);controller.abort();};
  },[organizationId,attempt]);
  // Scope during render too: cleanup alone cannot hide the previous organization's snapshot.
  const current=result?.organizationId===organizationId&&result.attempt===attempt?result:null;
  const data=current?.data;
  return <section className="panel" aria-labelledby="email-intake-settings-title">
    <h3 id="email-intake-settings-title">Email intake settings</h3>
    <p>Review the setup saved for this company. Connecting a mailbox and processing email are separate steps.</p>
    {!current&&<p role="status">Loading email settings…</p>}
    {current?.failed&&<p role="status">Email settings are not available right now. Try again, or contact ProFenz support if this continues.</p>}
    {data&&<>
      <h4>Saved setup</h4>
      <p>{data.channelState===null?'No forwarding setup has been saved.':savedLabel(channelLabels,data.channelState)??'The saved forwarding status is not recognized. Contact ProFenz support for help.'}</p>
      {data.connections.length===0?<p>No mailbox connections have been saved.</p>:<ul>{data.connections.map(connection=><li key={connection.id}>
        <strong>{connection.provider==='microsoft_365'?'Microsoft 365':connection.provider==='google_workspace'?'Google Workspace':'Email account'}: {connection.mailbox}</strong>
        <p>{savedLabel(connectionLabels,connection.state)??'The saved mailbox status is not recognized. Contact ProFenz support for help.'}</p>
      </li>)}</ul>}
      <h4>Email processing</h4>
      <p role="status">{data.processingInactive?'Email processing is not active.':'Email processing status needs confirmation.'}</p>
      <p>Use the existing document intake workflow for new work. Contact ProFenz support for help with email setup and next steps.</p>
    </>}
    {current&&<button type="button" onClick={()=>setAttempt(value=>value+1)}>Refresh email settings</button>}
  </section>;
}
